import { useSeoMeta } from "@unhead/react";
import { ArrowRight, ExternalLink, Image as ImageIcon } from "lucide-react";

import { SiteHeader } from "@/components/cashcraft/SiteHeader";
import { SiteFooter } from "@/components/cashcraft/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { PORTFOLIO } from "@/lib/cashcraft";

const Portfolio = () => {
  useSeoMeta({
    title: "Our work · BCH Launch",
    description:
      "A look at websites we've crafted — fast, modern, and built to accept Bitcoin Cash.",
  });

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute left-1/2 top-[-10%] h-[400px] w-[400px] -translate-x-1/2 rounded-full bg-primary/15 blur-[120px]" />
          </div>
          <div className="container mx-auto px-4 py-16 text-center sm:px-6 sm:py-20">
            <h1 className="mx-auto max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl">
              Our{" "}
              <span className="bg-gradient-to-r from-primary to-emerald-400 bg-clip-text text-transparent">
                work
              </span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
              Real products we've designed, built, and shipped — a connected
              ecosystem of fast, modern web apps. We don't just make websites;
              we build tools people actually use.
            </p>
          </div>
        </section>

        {/* Grid */}
        <section className="pb-20">
          <div className="container mx-auto px-4 sm:px-6">
            {PORTFOLIO.length === 0 ? (
              <Card className="mx-auto max-w-xl border-dashed">
                <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
                  <ImageIcon className="h-10 w-10 text-muted-foreground" />
                  <p className="max-w-sm text-muted-foreground">
                    Our portfolio is just getting started. Check back soon —
                    or be one of our first featured projects!
                  </p>
                  <Button asChild className="mt-2 rounded-full font-semibold">
                    <a href="/#start">
                      Start your project <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {PORTFOLIO.map((item, i) => (
                  <Card
                    key={i}
                    className="group overflow-hidden border-border/60 bg-card transition-all hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5"
                  >
                    <AspectRatio
                      ratio={16 / 10}
                      className="overflow-hidden border-b border-border/60 bg-muted"
                    >
                      <img
                        src={item.image}
                        alt={`${item.title} website screenshot`}
                        loading="lazy"
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105 motion-reduce:transform-none"
                      />
                    </AspectRatio>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-bold leading-tight">{item.title}</h3>
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 text-muted-foreground transition-colors hover:text-primary"
                            aria-label={`Visit ${item.title}`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                      <p className="mt-1.5 text-sm text-muted-foreground">
                        {item.description}
                      </p>
                      {item.tags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {item.tags.map((t) => (
                            <Badge key={t} variant="secondary" className="text-xs">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {PORTFOLIO.length > 0 && (
              <>
                <Card className="mx-auto mt-14 max-w-3xl border-primary/30 bg-primary/5">
                  <CardContent className="p-6 text-center sm:p-8">
                    <h3 className="text-xl font-bold">
                      One connected ecosystem
                    </h3>
                    <p className="mx-auto mt-2 max-w-2xl text-muted-foreground">
                      These products feed each other: people{" "}
                      <strong className="text-foreground">connect</strong> on BCH
                      Nostr, <strong className="text-foreground">learn</strong> on
                      BCH School,{" "}
                      <strong className="text-foreground">transact</strong> with
                      BCHPay, and{" "}
                      <strong className="text-foreground">discover</strong> local
                      merchants on BCH Local — a flywheel we designed, built, and
                      ship.
                    </p>
                  </CardContent>
                </Card>

                <div className="mt-12 text-center">
                  <p className="mb-4 text-muted-foreground">
                    Like what you see? Let's build yours.
                  </p>
                  <Button asChild size="lg" className="rounded-full px-8 font-semibold">
                    <a href="/#start">
                      Start your project <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Portfolio;
